/**
 * @fileoverview Unified EDA Dashboard Component
 * Uses existing EDA endpoints: /summary, /dictionary, /delivery-days, /price, /fulfillment, /outliers
 * Uses comprehensive endpoint: /comprehensive (schema, fact, integrity, distributions)
 * 9 Tabs: Quality | Dictionary | Univariate | Bivariate | Outliers | Schema | Fact | Integrity | Distributions
 */

import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ScatterChart, Scatter, Cell, PieChart, Pie,
  LineChart, Line, ComposedChart, Area
} from 'recharts';

// ═══════════════════════════════════════════════════════════════════════════
// AUTH HELPER
// ═══════════════════════════════════════════════════════════════════════════

const authFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = localStorage.getItem('xnuup_token');
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
};

// ═══════════════════════════════════════════════════════════════════════════
// TYPES - From _types.ts (Existing)
// ═══════════════════════════════════════════════════════════════════════════

interface ColumnStats {
  column_name: string;
  data_type: string;
  total_rows: number;
  null_count: number;
  null_pct: number;
  distinct_count: number;
  distinct_pct: number;
}

interface NumericStats {
  column_name: string;
  count: number;
  null_count: number;
  mean: number;
  std_dev: number;
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
  iqr: number;
  skewness: number;
  outlier_count: number;
  cv: number;
}

interface CategoricalStats {
  column_name: string;
  total_count: number;
  distinct_count: number;
  mode_value: string;
  mode_count: number;
  mode_pct: number;
  entropy: number;
  top_values: Array<{ value: string; count: number; pct: number }>;
}

interface DataQualityMetrics {
  total_rows: number;
  total_columns: number;
  completeness_pct: number;
  columns_with_nulls: number;
  duplicate_rows: number;
  data_freshness_hours: number;
  last_etl_batch: string;
  column_stats: ColumnStats[];
}

interface Correlation {
  column_x: string;
  column_y: string;
  correlation: number;
  sample_size: number;
}

interface BivariateGroupStats {
  group_by: string;
  group_value: string;
  target_mean: number;
  target_median: number;
  target_std: number;
  count: number;
}

interface FulfillmentGroupStats {
  group_value: string;
  total: number;
  delivered: number;
  rate: number;
}

interface OutlierSummary {
  column: string;
  outlier_count: number;
  outlier_pct: number;
  lower_bound: number;
  upper_bound: number;
}

interface DataDictionaryEntry {
  table_name: string;
  column_name: string;
  data_type: string;
  is_nullable: string;
  column_default: string | null;
  description: string;
  category: string;
}

interface TemporalDistribution {
  by_month: Array<{ period: string; count: number }>;
  by_day_of_week: Array<{ day: string; count: number }>;
}

interface EDASummary {
  generated_at: string;
  data_quality: DataQualityMetrics;
  univariate: {
    numeric: NumericStats[];
    categorical: CategoricalStats[];
    temporal: TemporalDistribution;
  };
  bivariate: {
    correlations: Correlation[];
  };
  outliers: OutlierSummary[];
}

// ═══════════════════════════════════════════════════════════════════════════
// TYPES - From comprehensive.ts
// ═══════════════════════════════════════════════════════════════════════════

interface SchemaTable {
  table_name: string;
  table_type: string;
  column_count: number;
  estimated_rows: number;
}

interface TableQuality {
  table_name: string;
  table_type: string;
  row_count: number;
  column_count: number;
  completeness_pct: number;
  columns_with_nulls: number;
  total_null_values: number;
}

interface IntegrityCheck {
  foreign_key: string;
  dimension: string;
  total_records: number;
  populated: number;
  orphans: number;
  coverage_pct: number;
  integrity: string;
}

interface DimensionStat {
  dimension: string;
  total_records: number;
  active_count?: number;
}

interface FactStats {
  total_records: number;
  unique_claims: number;
  unique_suppliers: number;
  unique_workshops: number;
  unique_insurers: number;
  unique_parts: number;
  unique_part_types: number;
  unique_vehicles: number;
  total_value: number;
  avg_price: number;
  avg_quantity: number;
  auto_quoted: number;
  auto_assigned: number;
  has_delivery_days: number;
  avg_delivery_days: number;
  earliest_date: string;
  latest_date: string;
}

interface Distribution {
  value: string;
  count: number;
  pct: number;
  total_value?: number;
}

interface ComprehensiveData {
  schema: SchemaTable[];
  quality: TableQuality[];
  fact_stats: FactStats;
  dimensions: DimensionStat[];
  integrity: IntegrityCheck[];
  distributions: Record<string, Distribution[]>;
  numeric_stats: any[];
  correlations: Correlation[];
  etl_metadata: any;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

const API_BASE = import.meta.env.VITE_API_URL || '/api';

const COLORS = [
  '#0ea5e9', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16'
];

const CATEGORY_COLORS: Record<string, string> = {
  Key: '#6366f1',
  Identifier: '#8b5cf6',
  Temporal: '#0ea5e9',
  Measure: '#22c55e',
  Flag: '#f59e0b',
  Attribute: '#64748b',
  Other: '#94a3b8'
};

const TABLE_TYPE_COLORS: Record<string, string> = {
  Fact: '#ef4444',
  Dimension: '#3b82f6',
  Aggregate: '#22c55e',
  Bridge: '#f59e0b',
  Staging: '#8b5cf6',
  Other: '#64748b'
};

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

const formatNumber = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return '0';
  return new Intl.NumberFormat('en-US').format(n);
};

const formatCurrency = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return '$0';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n);
};

// ═══════════════════════════════════════════════════════════════════════════
// HELPER COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

const InfoTooltip: React.FC<{ text: string }> = ({ text }) => {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-block ml-1">
      <button
        className="w-4 h-4 rounded-full bg-slate-200 text-slate-600 text-xs font-bold leading-none hover:bg-slate-300"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >i</button>
      {show && (
        <div className="absolute z-50 w-64 p-2 text-xs bg-slate-800 text-white rounded shadow-lg -left-28 top-6">
          {text}
        </div>
      )}
    </span>
  );
};

const StatCard: React.FC<{
  label: string;
  value: string | number;
  subtext?: string;
  color?: string;
  info?: string;
}> = ({ label, value, subtext, color = 'text-slate-900', info }) => (
  <div className="bg-white rounded-lg border border-slate-200 p-4">
    <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">
      {label}{info && <InfoTooltip text={info} />}
    </div>
    <div className={`text-2xl font-bold ${color}`}>{value}</div>
    {subtext && <div className="text-xs text-slate-400 mt-1">{subtext}</div>}
  </div>
);

const SectionHeader: React.FC<{ title: string; info?: string }> = ({ title, info }) => (
  <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
    {title}{info && <InfoTooltip text={info} />}
  </h2>
);

const Badge: React.FC<{ type: string; colors?: Record<string, string> }> = ({ type, colors = TABLE_TYPE_COLORS }) => (
  <span className="px-2 py-0.5 rounded text-xs font-medium text-white"
    style={{ backgroundColor: colors[type] || colors.Other || '#64748b' }}>
    {type}
  </span>
);

// ═══════════════════════════════════════════════════════════════════════════
// DATA QUALITY SECTION (From existing /summary endpoint)
// ═══════════════════════════════════════════════════════════════════════════

const DataQualitySection: React.FC<{ data: DataQualityMetrics }> = ({ data }) => {
  const [showAllColumns, setShowAllColumns] = useState(false);
  const nullColumns = data.column_stats.filter(c => c.null_pct > 0).sort((a, b) => b.null_pct - a.null_pct);
  const displayColumns = showAllColumns ? nullColumns : nullColumns.slice(0, 10);

  return (
    <div className="space-y-6">
      <SectionHeader title="Fact Table Quality" info="Data quality metrics for fact_part_order" />
      
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard label="Total Rows" value={formatNumber(data.total_rows)} />
        <StatCard label="Total Columns" value={data.total_columns} />
        <StatCard 
          label="Completeness" 
          value={`${data.completeness_pct}%`}
          color={data.completeness_pct >= 95 ? 'text-green-600' : data.completeness_pct >= 80 ? 'text-yellow-600' : 'text-red-600'}
        />
        <StatCard label="Cols w/ Nulls" value={data.columns_with_nulls} />
        <StatCard label="Duplicates" value={formatNumber(data.duplicate_rows)} />
        <StatCard 
          label="Freshness" 
          value={`${data.data_freshness_hours}h`}
          color={data.data_freshness_hours <= 2 ? 'text-green-600' : data.data_freshness_hours <= 24 ? 'text-yellow-600' : 'text-red-600'}
        />
      </div>

      {nullColumns.length > 0 && (
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="bg-slate-50 px-4 py-2 flex justify-between items-center">
            <span className="font-medium text-sm">Columns with Nulls</span>
            {nullColumns.length > 10 && (
              <button onClick={() => setShowAllColumns(!showAllColumns)} className="text-xs text-blue-600 hover:underline">
                {showAllColumns ? 'Show Less' : `Show All (${nullColumns.length})`}
              </button>
            )}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-3">Column</th>
                <th className="text-left p-3">Type</th>
                <th className="text-right p-3">Null Count</th>
                <th className="text-right p-3">Null %</th>
                <th className="text-right p-3">Distinct</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {displayColumns.map((col, idx) => (
                <tr key={idx} className="hover:bg-slate-50">
                  <td className="p-3 font-mono text-xs">{col.column_name}</td>
                  <td className="p-3 text-slate-500">{col.data_type}</td>
                  <td className="p-3 text-right">{formatNumber(col.null_count)}</td>
                  <td className={`p-3 text-right ${col.null_pct > 50 ? 'text-red-600' : col.null_pct > 10 ? 'text-yellow-600' : ''}`}>
                    {col.null_pct.toFixed(2)}%
                  </td>
                  <td className="p-3 text-right text-slate-500">{formatNumber(col.distinct_count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// DATA DICTIONARY SECTION (From existing /dictionary endpoint)
// ═══════════════════════════════════════════════════════════════════════════

const DataDictionarySection: React.FC<{ data: DataDictionaryEntry[] }> = ({ data }) => {
  const [filterTable, setFilterTable] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('');

  const tables = [...new Set(data.map(d => d.table_name))];
  const categories = [...new Set(data.map(d => d.category))];
  const filtered = data.filter(d => 
    (!filterTable || d.table_name === filterTable) &&
    (!filterCategory || d.category === filterCategory)
  );

  return (
    <div className="space-y-6">
      <SectionHeader title="Data Dictionary" info="Schema documentation for all tables" />
      
      <div className="flex gap-4 flex-wrap">
        <select value={filterTable} onChange={(e) => setFilterTable(e.target.value)} className="px-3 py-2 border rounded text-sm">
          <option value="">All Tables</option>
          {tables.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="px-3 py-2 border rounded text-sm">
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="max-h-[600px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="text-left p-3">Table</th>
                <th className="text-left p-3">Column</th>
                <th className="text-left p-3">Type</th>
                <th className="text-center p-3">Nullable</th>
                <th className="text-center p-3">Category</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((entry, idx) => (
                <tr key={idx} className="hover:bg-slate-50">
                  <td className="p-3 font-mono text-xs">{entry.table_name}</td>
                  <td className="p-3 font-mono text-xs">{entry.column_name}</td>
                  <td className="p-3 text-slate-500">{entry.data_type}</td>
                  <td className="p-3 text-center">{entry.is_nullable === 'YES' ? '✓' : ''}</td>
                  <td className="p-3 text-center"><Badge type={entry.category} colors={CATEGORY_COLORS} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// UNIVARIATE SECTION (From existing /summary endpoint)
// ═══════════════════════════════════════════════════════════════════════════

const UnivariateSection: React.FC<{
  numeric: NumericStats[];
  categorical: CategoricalStats[];
  temporal: TemporalDistribution;
}> = ({ numeric, categorical, temporal }) => {
  const [activeSubTab, setActiveSubTab] = useState<'numeric' | 'categorical' | 'temporal'>('numeric');

  return (
    <div className="space-y-6">
      <SectionHeader title="Univariate Analysis" info="Distribution analysis for individual columns" />
      
      <div className="flex gap-2">
        {(['numeric', 'categorical', 'temporal'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveSubTab(tab)}
            className={`px-4 py-2 rounded text-sm ${activeSubTab === tab ? 'bg-blue-600 text-white' : 'bg-slate-100 hover:bg-slate-200'}`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {activeSubTab === 'numeric' && (
        <div className="bg-white rounded-lg border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-3">Column</th>
                <th className="text-right p-3">Count</th>
                <th className="text-right p-3">Mean</th>
                <th className="text-right p-3">Std</th>
                <th className="text-right p-3">Min</th>
                <th className="text-right p-3">Q1</th>
                <th className="text-right p-3">Median</th>
                <th className="text-right p-3">Q3</th>
                <th className="text-right p-3">Max</th>
                <th className="text-right p-3">Outliers</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {numeric.map((s, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="p-3 font-mono text-xs">{s.column_name}</td>
                  <td className="p-3 text-right">{formatNumber(s.count)}</td>
                  <td className="p-3 text-right">{s.mean?.toFixed(2)}</td>
                  <td className="p-3 text-right text-slate-500">{s.std_dev?.toFixed(2)}</td>
                  <td className="p-3 text-right">{s.min?.toFixed(2)}</td>
                  <td className="p-3 text-right text-slate-500">{s.q1?.toFixed(2)}</td>
                  <td className="p-3 text-right font-medium">{s.median?.toFixed(2)}</td>
                  <td className="p-3 text-right text-slate-500">{s.q3?.toFixed(2)}</td>
                  <td className="p-3 text-right">{s.max?.toFixed(2)}</td>
                  <td className="p-3 text-right">{formatNumber(s.outlier_count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeSubTab === 'categorical' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {categorical.map((cat, i) => (
            <div key={i} className="bg-white rounded-lg border p-4">
              <div className="font-medium mb-2">{cat.column_name}</div>
              <div className="text-xs text-slate-500 mb-3">
                {formatNumber(cat.distinct_count)} distinct | Mode: {cat.mode_value} ({cat.mode_pct}%)
              </div>
              <div className="space-y-1">
                {cat.top_values?.slice(0, 5).map((v, j) => (
                  <div key={j} className="flex items-center gap-2">
                    <div className="w-24 text-xs truncate">{v.value || 'NULL'}</div>
                    <div className="flex-1 bg-slate-100 rounded h-4">
                      <div className="bg-blue-500 h-4 rounded" style={{ width: `${Math.min(v.pct, 100)}%` }} />
                    </div>
                    <div className="w-16 text-xs text-right">{v.pct}%</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeSubTab === 'temporal' && temporal && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {temporal.by_month?.length > 0 && (
            <div className="bg-white rounded-lg border p-4">
              <div className="font-medium mb-4">Orders by Month</div>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={temporal.by_month}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                  <YAxis tickFormatter={(v) => formatNumber(v)} />
                  <Tooltip formatter={(v: number) => formatNumber(v)} />
                  <Bar dataKey="count" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {temporal.by_day_of_week?.length > 0 && (
            <div className="bg-white rounded-lg border p-4">
              <div className="font-medium mb-4">Orders by Day of Week</div>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={temporal.by_day_of_week}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis tickFormatter={(v) => formatNumber(v)} />
                  <Tooltip formatter={(v: number) => formatNumber(v)} />
                  <Bar dataKey="count" fill="#22c55e" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// BIVARIATE SECTION (From existing /delivery-days, /price, /fulfillment)
// ═══════════════════════════════════════════════════════════════════════════

const BivariateSection: React.FC<{
  correlations: Correlation[];
  deliveryByCategory: Record<string, BivariateGroupStats[]>;
  priceByCategory: Record<string, BivariateGroupStats[]>;
  fulfillmentByCategory: Record<string, FulfillmentGroupStats[]>;
}> = ({ correlations, deliveryByCategory, priceByCategory, fulfillmentByCategory }) => {
  return (
    <div className="space-y-6">
      <SectionHeader title="Bivariate Analysis" info="Relationships between variables" />
      
      {correlations.length > 0 && (
        <div className="bg-white rounded-lg border p-4">
          <div className="font-medium mb-4">Numeric Correlations</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {correlations.slice(0, 12).map((c, i) => (
              <div key={i} className="bg-slate-50 rounded p-3">
                <div className="text-xs text-slate-500">{c.column_x} × {c.column_y}</div>
                <div className={`text-xl font-bold ${
                  Math.abs(c.correlation) >= 0.7 ? 'text-purple-600' :
                  Math.abs(c.correlation) >= 0.4 ? 'text-blue-600' : 'text-slate-600'
                }`}>
                  {c.correlation.toFixed(4)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {Object.keys(deliveryByCategory).length > 0 && (
        <div className="bg-white rounded-lg border p-4">
          <div className="font-medium mb-4">Delivery Days by Category</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(deliveryByCategory).map(([key, data]) => (
              <div key={key}>
                <div className="text-sm text-slate-600 mb-2">{key.replace('by_', '').replace('_', ' ')}</div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={data} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="group_value" width={100} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="target_mean" fill="#0ea5e9" name="Mean Days" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ))}
          </div>
        </div>
      )}

      {Object.keys(fulfillmentByCategory).length > 0 && (
        <div className="bg-white rounded-lg border p-4">
          <div className="font-medium mb-4">Fulfillment Rate by Category</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(fulfillmentByCategory).map(([key, data]) => (
              <div key={key}>
                <div className="text-sm text-slate-600 mb-2">{key.replace('by_', '').replace('_', ' ')}</div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={data} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 100]} />
                    <YAxis type="category" dataKey="group_value" width={100} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="rate" fill="#22c55e" name="Fulfillment %">
                      {data.map((entry, index) => (
                        <Cell key={index} fill={entry.rate >= 80 ? '#22c55e' : entry.rate >= 50 ? '#f59e0b' : '#ef4444'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// OUTLIERS SECTION (From existing /outliers endpoint)
// ═══════════════════════════════════════════════════════════════════════════

const OutliersSection: React.FC<{ data: OutlierSummary[] }> = ({ data }) => (
  <div className="space-y-6">
    <SectionHeader title="Outlier Detection" info="IQR-based outlier analysis (1.5×IQR rule)" />
    <div className="bg-white rounded-lg border overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50">
          <tr>
            <th className="text-left p-3">Column</th>
            <th className="text-right p-3">Outlier Count</th>
            <th className="text-right p-3">Outlier %</th>
            <th className="text-right p-3">Lower Bound</th>
            <th className="text-right p-3">Upper Bound</th>
            <th className="text-center p-3">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {data.map((row, idx) => (
            <tr key={idx} className="hover:bg-slate-50">
              <td className="p-3 font-mono text-xs">{row.column}</td>
              <td className="p-3 text-right">{formatNumber(row.outlier_count)}</td>
              <td className={`p-3 text-right ${row.outlier_pct > 5 ? 'text-red-600' : row.outlier_pct > 1 ? 'text-yellow-600' : 'text-green-600'}`}>
                {row.outlier_pct.toFixed(2)}%
              </td>
              <td className="p-3 text-right font-mono text-slate-500">{row.lower_bound.toLocaleString()}</td>
              <td className="p-3 text-right font-mono text-slate-500">{row.upper_bound.toLocaleString()}</td>
              <td className="p-3 text-center">
                {row.outlier_pct > 5 ? (
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">High</span>
                ) : row.outlier_pct > 1 ? (
                  <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">Medium</span>
                ) : (
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">Normal</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// SCHEMA SECTION (From /comprehensive)
// ═══════════════════════════════════════════════════════════════════════════

const SchemaSection: React.FC<{ data: SchemaTable[] }> = ({ data }) => {
  const totalRows = data.reduce((sum, t) => sum + t.estimated_rows, 0);

  return (
    <div className="space-y-6">
      <SectionHeader title="Schema Explorer" info="All tables in data warehouse" />
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Tables" value={data.length} color="text-blue-600" />
        <StatCard label="Fact Tables" value={data.filter(t => t.table_type === 'Fact').length} color="text-red-600" />
        <StatCard label="Dimension Tables" value={data.filter(t => t.table_type === 'Dimension').length} color="text-blue-600" />
        <StatCard label="Total Rows (Est.)" value={formatNumber(totalRows)} color="text-green-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="bg-slate-50 px-4 py-2 font-semibold text-sm">Tables</div>
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left p-2">Table</th>
                  <th className="text-center p-2">Type</th>
                  <th className="text-right p-2">Cols</th>
                  <th className="text-right p-2">Rows</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.map((t, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="p-2 font-mono text-xs">{t.table_name}</td>
                    <td className="p-2 text-center"><Badge type={t.table_type} /></td>
                    <td className="p-2 text-right">{t.column_count}</td>
                    <td className="p-2 text-right">{formatNumber(t.estimated_rows)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg border p-4">
          <div className="font-semibold text-sm mb-4">Row Distribution</div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.filter(t => t.estimated_rows > 0).slice(0, 10)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tickFormatter={(v) => formatNumber(v)} />
              <YAxis type="category" dataKey="table_name" width={120} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v: number) => formatNumber(v)} />
              <Bar dataKey="estimated_rows" name="Rows">
                {data.slice(0, 10).map((entry, index) => (
                  <Cell key={index} fill={TABLE_TYPE_COLORS[entry.table_type] || COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// FACT ANALYSIS SECTION (From /comprehensive)
// ═══════════════════════════════════════════════════════════════════════════

const FactAnalysisSection: React.FC<{ factStats: FactStats; dimensions: DimensionStat[] }> = ({ factStats, dimensions }) => {
  const autoQuotePct = factStats.total_records > 0 ? ((factStats.auto_quoted / factStats.total_records) * 100).toFixed(1) : '0';
  const autoAssignPct = factStats.total_records > 0 ? ((factStats.auto_assigned / factStats.total_records) * 100).toFixed(1) : '0';

  return (
    <div className="space-y-6">
      <SectionHeader title="Fact Table Analysis" info="Key metrics from fact_part_order" />
      
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard label="Total Records" value={formatNumber(factStats.total_records)} color="text-blue-600" />
        <StatCard label="Unique Claims" value={formatNumber(factStats.unique_claims)} />
        <StatCard label="Unique Suppliers" value={formatNumber(factStats.unique_suppliers)} />
        <StatCard label="Unique Workshops" value={formatNumber(factStats.unique_workshops)} />
        <StatCard label="Total Value" value={formatCurrency(factStats.total_value)} color="text-green-600" />
        <StatCard label="Avg Price" value={formatCurrency(factStats.avg_price)} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Auto Quoted" value={`${autoQuotePct}%`} subtext={formatNumber(factStats.auto_quoted)} color="text-teal-600" />
        <StatCard label="Auto Assigned" value={`${autoAssignPct}%`} subtext={formatNumber(factStats.auto_assigned)} color="text-indigo-600" />
        <StatCard label="Avg Delivery Days" value={factStats.avg_delivery_days?.toFixed(1) || '-'} />
        <StatCard label="Date Range" value={`${factStats.earliest_date?.slice(0, 10) || '-'} to ${factStats.latest_date?.slice(0, 10) || '-'}`} />
      </div>

      {dimensions.length > 0 && (
        <div className="bg-white rounded-lg border p-4">
          <div className="font-semibold text-sm mb-4">Dimension Summary</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {dimensions.map((dim, i) => (
              <div key={i} className="bg-slate-50 rounded p-3">
                <div className="text-xs text-slate-500 uppercase">{dim.dimension.replace('dim_', '')}</div>
                <div className="text-xl font-bold text-slate-800">{formatNumber(dim.total_records)}</div>
                {dim.active_count !== undefined && (
                  <div className="text-xs text-green-600">{formatNumber(dim.active_count)} active</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// INTEGRITY SECTION (From /comprehensive)
// ═══════════════════════════════════════════════════════════════════════════

const IntegritySection: React.FC<{ data: IntegrityCheck[] }> = ({ data }) => {
  const issueCount = data.filter(c => c.orphans > 0).length;

  return (
    <div className="space-y-6">
      <SectionHeader title="Referential Integrity" info="Foreign key validation" />
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="FK Checks" value={data.length} />
        <StatCard label="Issues" value={issueCount} color={issueCount > 0 ? 'text-red-600' : 'text-green-600'} />
        <StatCard label="Avg Coverage" value={`${(data.reduce((sum, c) => sum + c.coverage_pct, 0) / data.length).toFixed(1)}%`} />
        <StatCard label="Total Orphans" value={formatNumber(data.reduce((sum, c) => sum + c.orphans, 0))} color={data.some(c => c.orphans > 0) ? 'text-red-600' : 'text-green-600'} />
      </div>

      <div className="bg-white rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left p-3">Foreign Key</th>
              <th className="text-left p-3">Dimension</th>
              <th className="text-right p-3">Total</th>
              <th className="text-right p-3">Populated</th>
              <th className="text-right p-3">Coverage</th>
              <th className="text-right p-3">Orphans</th>
              <th className="text-center p-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.map((check, i) => (
              <tr key={i} className="hover:bg-slate-50">
                <td className="p-3 font-mono text-xs">{check.foreign_key}</td>
                <td className="p-3 font-mono text-xs">{check.dimension}</td>
                <td className="p-3 text-right">{formatNumber(check.total_records)}</td>
                <td className="p-3 text-right">{formatNumber(check.populated)}</td>
                <td className={`p-3 text-right font-medium ${check.coverage_pct >= 99 ? 'text-green-600' : check.coverage_pct >= 80 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {check.coverage_pct.toFixed(1)}%
                </td>
                <td className={`p-3 text-right ${check.orphans > 0 ? 'text-red-600 font-medium' : ''}`}>
                  {formatNumber(check.orphans)}
                </td>
                <td className="p-3 text-center">
                  {check.integrity === 'OK' ? (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">OK</span>
                  ) : (
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">Orphans</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// DISTRIBUTIONS SECTION (From /comprehensive)
// ═══════════════════════════════════════════════════════════════════════════

const DistributionsSection: React.FC<{ data: Record<string, Distribution[]> }> = ({ data }) => {
  const [selectedDist, setSelectedDist] = useState<string>('status');
  const distributions = Object.keys(data).filter(k => data[k]?.length > 0);

  return (
    <div className="space-y-6">
      <SectionHeader title="Value Distributions" info="Distribution of categorical values" />
      
      <div className="flex gap-2 flex-wrap">
        {distributions.map(key => (
          <button
            key={key}
            onClick={() => setSelectedDist(key)}
            className={`px-3 py-1 rounded text-sm ${selectedDist === key ? 'bg-blue-600 text-white' : 'bg-slate-100 hover:bg-slate-200'}`}
          >
            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </button>
        ))}
      </div>

      {data[selectedDist] && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left p-3">Value</th>
                    <th className="text-right p-3">Count</th>
                    <th className="text-right p-3">%</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data[selectedDist].map((row, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="p-3">{row.value || 'Unknown'}</td>
                      <td className="p-3 text-right">{formatNumber(row.count)}</td>
                      <td className="p-3 text-right text-slate-500">{row.pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg border p-4">
            <ResponsiveContainer width="100%" height={300}>
              {selectedDist === 'monthly_trend' ? (
                <ComposedChart data={data[selectedDist]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="value" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="left" tickFormatter={(v) => formatNumber(v)} />
                  <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => formatCurrency(v)} />
                  <Tooltip formatter={(v: number, name: string) => [name === 'count' ? formatNumber(v) : formatCurrency(v), name === 'count' ? 'Orders' : 'Value']} />
                  <Bar yAxisId="left" dataKey="count" fill="#3b82f6" name="count" />
                  <Line yAxisId="right" type="monotone" dataKey="total_value" stroke="#22c55e" name="total_value" />
                </ComposedChart>
              ) : (
                <PieChart>
                  <Pie
                    data={data[selectedDist].slice(0, 8)}
                    dataKey="count"
                    nameKey="value"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ pct }) => `${pct}%`}
                    labelLine={false}
                  >
                    {data[selectedDist].slice(0, 8).map((_, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: number) => formatNumber(v)} />
                  <Legend />
                </PieChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN EDA DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════

type TabId = 'quality' | 'dictionary' | 'univariate' | 'bivariate' | 'outliers' | 'schema' | 'fact' | 'integrity' | 'distributions';

const EDADashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('quality');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Existing EDA data (from /summary, /dictionary, /delivery-days, /price, /fulfillment)
  const [edaData, setEdaData] = useState<EDASummary | null>(null);
  const [dictionary, setDictionary] = useState<DataDictionaryEntry[]>([]);
  const [deliveryByCategory, setDeliveryByCategory] = useState<Record<string, BivariateGroupStats[]>>({});
  const [priceByCategory, setPriceByCategory] = useState<Record<string, BivariateGroupStats[]>>({});
  const [fulfillmentByCategory, setFulfillmentByCategory] = useState<Record<string, FulfillmentGroupStats[]>>({});

  // Comprehensive data (from /comprehensive)
  const [comprehensiveData, setComprehensiveData] = useState<ComprehensiveData | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load existing EDA summary
      const summaryRes = await authFetch(`${API_BASE}/eda/summary`);
      if (summaryRes.ok) {
        const summaryData = await summaryRes.json();
        setEdaData(summaryData.data);
      }

      // Load dictionary
      const dictRes = await authFetch(`${API_BASE}/eda/dictionary`);
      if (dictRes.ok) {
        const dictData = await dictRes.json();
        setDictionary(dictData.data || []);
      }

      // Load bivariate data
      const [deliveryRes, priceRes, fulfillmentRes] = await Promise.all([
        authFetch(`${API_BASE}/eda/delivery-days`),
        authFetch(`${API_BASE}/eda/price`),
        authFetch(`${API_BASE}/eda/fulfillment`)
      ]);

      if (deliveryRes.ok) {
        const d = await deliveryRes.json();
        setDeliveryByCategory(d.data || {});
      }
      if (priceRes.ok) {
        const p = await priceRes.json();
        setPriceByCategory(p.data || {});
      }
      if (fulfillmentRes.ok) {
        const f = await fulfillmentRes.json();
        setFulfillmentByCategory(f.data || {});
      }

      // Load comprehensive data
    const compRes = await authFetch(`${API_BASE}/eda/comprehensive`);
      if (compRes.ok) {
        const compData = await compRes.json();
        setComprehensiveData(compData.data);
      } else {
        console.error('Comprehensive API failed:', compRes.status);
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const tabs: Array<{ id: TabId; label: string }> = [
    { id: 'quality', label: 'Data Quality' },
    { id: 'dictionary', label: 'Dictionary' },
    { id: 'univariate', label: 'Univariate' },
    { id: 'bivariate', label: 'Bivariate' },
    { id: 'outliers', label: 'Outliers' },
    { id: 'schema', label: 'Schema' },
    { id: 'fact', label: 'Fact Analysis' },
    { id: 'integrity', label: 'Integrity' },
    { id: 'distributions', label: 'Distributions' }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-slate-900">Exploratory Data Analysis</h1>
              <p className="text-sm text-slate-500">XNuuP Data Warehouse Quality & Statistics</p>
            </div>
            <button
              onClick={loadData}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          <nav className="flex gap-1 mt-4 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-t text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id 
                    ? 'bg-slate-100 text-blue-600 border-b-2 border-blue-600' 
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      {error && (
        <div className="max-w-7xl mx-auto px-4 mt-4">
          <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
            Error: {error}
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 py-6">
        {loading && !edaData && !comprehensiveData ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-slate-500">Loading EDA data...</div>
          </div>
        ) : (
          <>
            {/* Existing tabs */}
            {activeTab === 'quality' && edaData && <DataQualitySection data={edaData.data_quality} />}
            {activeTab === 'dictionary' && <DataDictionarySection data={dictionary} />}
            {activeTab === 'univariate' && edaData && (
              <UnivariateSection 
                numeric={edaData.univariate.numeric}
                categorical={edaData.univariate.categorical}
                temporal={edaData.univariate.temporal}
              />
            )}
            {activeTab === 'bivariate' && edaData && (
              <BivariateSection 
                correlations={edaData.bivariate.correlations}
                deliveryByCategory={deliveryByCategory}
                priceByCategory={priceByCategory}
                fulfillmentByCategory={fulfillmentByCategory}
              />
            )}
            {activeTab === 'outliers' && edaData && <OutliersSection data={edaData.outliers} />}

            {/* Comprehensive tabs */}
            {activeTab === 'schema' && comprehensiveData && <SchemaSection data={comprehensiveData.schema} />}
            {activeTab === 'fact' && comprehensiveData && (
              <FactAnalysisSection factStats={comprehensiveData.fact_stats} dimensions={comprehensiveData.dimensions} />
            )}
            {activeTab === 'integrity' && comprehensiveData && <IntegritySection data={comprehensiveData.integrity} />}
            {activeTab === 'distributions' && comprehensiveData && <DistributionsSection data={comprehensiveData.distributions} />}
          </>
        )}
      </main>
    </div>
  );
};

export default EDADashboard;